/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Search, Star, TrendingUp, Zap, ArrowRight, PhoneCall, Grid, List } from 'lucide-react';
import { publicApi } from '../utils/api';

const CATEGORY_ICONS = {
  // DB slugs (canonical)
  'writing-assistants': '✍️',
  'image-generators': '🎨',
  'video-creation': '🎬',
  'code-assistants': '💻',
  'chatbots-agents': '🤖',
  'seo-tools': '📈',
  'ai-agents': '🤖',
  'workflow-automation': '⚡',
  'audio-music': '🎵',
  'data-analysis': '📊',
  'email-productivity': '📧',
  'marketing-ads': '📱',
  '3d-design': '🎭',
  'translation-languages': '🌐',
  'operations-tracking': '📊',
  // Alias slugs for backward compatibility
  'image-generation': '🎨',
  'chatbots': '🤖',
  'seo-marketing': '📈',
  'audio-voice': '🎙️',
  'data-analytics': '📊',
  'productivity': '🗂️',
  'customer-support': '💬',
  'sales-crm': '🤝',
  'design': '🖌️',
  'research': '🔬',
};

const ACCENT_PALETTE = [
  '#00ff87', '#FFE400', '#a78bfa', '#f97316',
  '#06b6d4', '#ec4899', '#34d399', '#fb923c',
  '#818cf8', '#f43f5e', '#4ade80', '#38bdf8',
  '#c084fc', '#fbbf24',
];

const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'alpha', label: 'A → Z' },
  { value: 'tools', label: 'Most Tools' },
];

const CATEGORY_PAGE_CACHE_KEY = 'wowrolla_categories_page_cache_v1';
const CATEGORY_PAGE_CACHE_TTL = 5 * 60 * 1000;

const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const buildCategoryFallback = (tools = []) => {
  const byId = new Map();
  tools.forEach((tool) => {
    const id = tool.category_id || tool.category_slug || slugify(tool.category_name || 'ai-tools');
    const name = tool.category_name || 'AI Tools';
    const slug = tool.category_slug || slugify(name);

    if (!byId.has(id)) {
      byId.set(id, {
        id,
        name,
        slug,
        description: '',
      });
    }
  });
  return Array.from(byId.values());
};

const readCategoriesCache = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(CATEGORY_PAGE_CACHE_KEY) || 'null');
    if (!cached || typeof cached !== 'object') return null;
    if (Date.now() - Number(cached.ts || 0) > CATEGORY_PAGE_CACHE_TTL) return null;
    return cached;
  } catch {
    return null;
  }
};

const writeCategoriesCache = (payload) => {
  try {
    localStorage.setItem(
      CATEGORY_PAGE_CACHE_KEY,
      JSON.stringify({
        ts: Date.now(),
        ...payload,
      })
    );
  } catch {
    // no-op if storage is unavailable
  }
};

const buildCategoryToolsMap = (catList = [], allTools = []) => {
  const toolsMap = {};
  const byId = new Map();
  const bySlug = new Map();
  const byName = new Map();

  catList.forEach((cat) => {
    toolsMap[cat.id] = [];
    byId.set(String(cat.id || '').toLowerCase(), cat.id);
    bySlug.set(String(cat.slug || '').toLowerCase(), cat.id);
    byName.set(String(cat.name || '').toLowerCase(), cat.id);
  });

  allTools.forEach((tool) => {
    const idKey = String(tool.category_id || '').toLowerCase();
    const slugKey = String(tool.category_slug || '').toLowerCase();
    const nameKey = String(tool.category_name || '').toLowerCase();

    const resolvedCategoryId = byId.get(idKey) || bySlug.get(slugKey) || byName.get(nameKey);
    if (resolvedCategoryId && toolsMap[resolvedCategoryId]) {
      toolsMap[resolvedCategoryId].push(tool);
    }
  });

  return toolsMap;
};

const applyToolCounts = (catList = [], toolsMap = {}) =>
  catList.map((cat) => ({
    ...cat,
    tool_count: toolsMap[cat.id]?.length ?? cat.tool_count ?? 0,
  }));

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [categoryTools, setCategoryTools] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('popular');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  useEffect(() => {
    let mounted = true;

    const cached = readCategoriesCache();
    if (cached?.categories && cached?.categoryTools) {
      setCategories(cached.categories);
      setCategoryTools(cached.categoryTools);
      setLoading(false);
    }

    (async () => {
      try {
        const [catResult, allToolsResult] = await Promise.allSettled([
          publicApi.getCategories(),
          publicApi.getAllTools(false),
        ]);

        const allTools = allToolsResult.status === 'fulfilled' ? (allToolsResult.value.data || []) : [];
        const catList = catResult.status === 'fulfilled'
          ? (catResult.value.data || [])
          : buildCategoryFallback(allTools);
        const toolsMap = buildCategoryToolsMap(catList, allTools);
        const categoriesWithCounts = applyToolCounts(catList, toolsMap);

        if (!mounted) return;
        setCategories(categoriesWithCounts);
        setCategoryTools(toolsMap);
        writeCategoriesCache({
          categories: categoriesWithCounts,
          categoryTools: toolsMap,
        });
      } catch (err) {
        console.error('Failed to load categories', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    let list = [...categories];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q)
      );
    }
    if (sort === 'alpha') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === 'tools') list.sort((a, b) => (categoryTools[b.id]?.length || 0) - (categoryTools[a.id]?.length || 0));
    return list;
  }, [categories, categoryTools, search, sort]);

  const topCategories = useMemo(
    () => [...categories].sort((a, b) => (categoryTools[b.id]?.length || 0) - (categoryTools[a.id]?.length || 0)).slice(0, 4),
    [categories, categoryTools]
  );

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-robot" data-testid="categories-page">
      <Helmet>
        <title>All AI Tool Categories | Haiystack</title>
        <meta name="description" content="Browse all AI tool categories — from writing assistants to AI agents. Filter, compare, and find the right tool for every workflow." />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden py-16 mb-2" style={{ background: 'linear-gradient(135deg, rgba(0,255,135,0.04) 0%, rgba(255,228,0,0.04) 100%)' }}>
        <div className="absolute inset-0 prime-aurora-bg opacity-40" />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs font-mono uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--accent-primary)' }}>
              AI Tool Directory
            </p>
            <h1 className="text-4xl sm:text-5xl font-black mb-4 text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              All AI Categories
            </h1>
            <p className="text-lg max-w-2xl mb-8" style={{ color: 'var(--text-secondary)' }}>
              {categories.length > 0 ? categories.length : '14'}+ categories covering every AI workflow — reviewed, ranked, and scored for real-world ROI.
            </p>

            {/* Search + Sort bar */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search categories..."
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                  style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)', color: 'var(--text-primary)' }}
                />
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="px-4 py-3 rounded-xl text-sm"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)', color: 'var(--text-primary)' }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-strong)' }}>
                <button
                  onClick={() => setViewMode('grid')}
                  className="px-3 py-3 transition-colors"
                  style={{ background: viewMode === 'grid' ? 'var(--accent-primary)' : 'var(--bg-elevated)', color: viewMode === 'grid' ? '#000' : 'var(--text-secondary)' }}
                  title="Grid view"
                >
                  <Grid size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className="px-3 py-3 transition-colors"
                  style={{ background: viewMode === 'list' ? 'var(--accent-primary)' : 'var(--bg-elevated)', color: viewMode === 'list' ? '#000' : 'var(--text-secondary)' }}
                  title="List view"
                >
                  <List size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick picks strip */}
      {!loading && topCategories.length > 0 && (
        <section className="container mx-auto px-6 mb-8">
          <p className="text-xs font-mono uppercase tracking-[0.16em] mb-3" style={{ color: 'var(--text-muted)' }}>Most Explored</p>
          <div className="flex flex-wrap gap-2">
            {topCategories.map((cat, i) => (
              <Link
                key={cat.id}
                to={`/categories/${cat.slug}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                style={{ background: `${ACCENT_PALETTE[i]}18`, border: `1px solid ${ACCENT_PALETTE[i]}40`, color: ACCENT_PALETTE[i] }}
              >
                <span>{CATEGORY_ICONS[cat.slug] || '🔧'}</span>
                {cat.name}
                <span className="text-xs opacity-60">{categoryTools[cat.id]?.length || 0} tools</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Main grid / list */}
      <section className="container mx-auto px-6 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-lg mb-2" style={{ color: 'var(--text-secondary)' }}>No categories match "{search}"</p>
            <button onClick={() => setSearch('')} className="text-sm underline" style={{ color: 'var(--accent-primary)' }}>Clear search</button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((cat, idx) => {
              const accent = ACCENT_PALETTE[idx % ACCENT_PALETTE.length];
              const tools = categoryTools[cat.id] || [];
              const topThree = tools.slice(0, 3);
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04, duration: 0.35 }}
                >
                  <Link
                    to={`/categories/${cat.slug}`}
                    className="block h-full rounded-2xl p-5 transition-all hover:scale-[1.02] hover:shadow-xl"
                    style={{ background: 'var(--bg-elevated)', border: `1px solid ${accent}30` }}
                  >
                    {/* Category header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-3xl mb-2">{CATEGORY_ICONS[cat.slug] || '🔧'}</div>
                        <h2 className="font-bold text-base leading-tight" style={{ color: 'var(--text-primary)' }}>{cat.name}</h2>
                      </div>
                      <span className="text-xs font-mono px-2 py-1 rounded-md mt-1" style={{ background: `${accent}20`, color: accent }}>
                        {tools.length} tools
                      </span>
                    </div>

                    {/* Description */}
                    {cat.description && (
                      <p className="text-sm mb-4 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{cat.description}</p>
                    )}

                    {/* Top tools preview */}
                    {topThree.length > 0 && (
                      <div className="space-y-1.5 mb-4">
                        {topThree.map((tool) => (
                          <div key={tool.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded-md" style={{ background: 'rgba(255,255,255,0.04)' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>{tool.name}</span>
                            {tool.rating?.overall && (
                              <span className="flex items-center gap-1" style={{ color: accent }}>
                                <Star size={10} fill={accent} />
                                {tool.rating.overall}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: accent }}>
                      Explore <ArrowRight size={12} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        ) : (
          /* List view */
          <div className="space-y-3">
            {filtered.map((cat, idx) => {
              const accent = ACCENT_PALETTE[idx % ACCENT_PALETTE.length];
              const tools = categoryTools[cat.id] || [];
              const avgRating = tools.reduce((acc, t) => acc + (t.rating?.overall || 0), 0) / (tools.filter(t => t.rating?.overall).length || 1);
              return (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.03, duration: 0.3 }}
                >
                  <Link
                    to={`/categories/${cat.slug}`}
                    className="flex items-center justify-between gap-4 p-4 rounded-xl transition-all hover:scale-[1.01]"
                    style={{ background: 'var(--bg-elevated)', border: `1px solid ${accent}25` }}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="text-2xl flex-shrink-0">{CATEGORY_ICONS[cat.slug] || '🔧'}</div>
                      <div className="min-w-0">
                        <h2 className="font-bold truncate" style={{ color: 'var(--text-primary)' }}>{cat.name}</h2>
                        {cat.description && (
                          <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>{cat.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-5 flex-shrink-0 text-sm">
                      <div className="text-center">
                        <div className="font-bold" style={{ color: accent }}>{tools.length}</div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>tools</div>
                      </div>
                      {avgRating > 0 && (
                        <div className="text-center">
                          <div className="flex items-center gap-1 font-bold" style={{ color: '#FFE400' }}><Star size={12} fill="#FFE400" />{avgRating.toFixed(1)}</div>
                          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>avg</div>
                        </div>
                      )}
                      <ArrowRight size={16} style={{ color: accent }} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Priming stats strip */}
        {!loading && (
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: <Grid size={18} />, value: `${categories.length}+`, label: 'Categories covered', color: '#00ff87' },
              { icon: <TrendingUp size={18} />, value: `${Object.values(categoryTools).reduce((a, b) => a + b.length, 0)}+`, label: 'Tools indexed', color: '#FFE400' },
              { icon: <Star size={18} />, value: '4.7', label: 'Avg tool rating', color: '#a78bfa' },
              { icon: <Zap size={18} />, value: '18–35%', label: 'Avg ROI gain', color: '#f97316' },
            ].map((s, i) => (
              <div key={i} className="p-4 rounded-xl text-center" style={{ background: 'var(--bg-elevated)', border: `1px solid ${s.color}25` }}>
                <div className="flex justify-center mb-2" style={{ color: s.color }}>{s.icon}</div>
                <div className="text-2xl font-black mb-1" style={{ color: s.color }}>{s.value}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Consulting promo banner */}
        <div
          className="mt-12 p-6 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-5"
          style={{ background: 'linear-gradient(135deg, rgba(255,232,0,0.07), rgba(0,255,135,0.04))', border: '1px solid rgba(255,232,0,0.25)' }}
        >
          <div>
            <h3 className="font-bold text-lg mb-1 text-white">Not sure which category fits your workflow?</h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Our consulting service maps your team's processes to the right AI stack — saving weeks of evaluation time.
            </p>
          </div>
          <Link
            to="/consulting#book"
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all hover:scale-105"
            style={{ border: '1px solid rgba(255,232,0,0.65)', color: '#FFE400', background: 'rgba(255,232,0,0.1)', boxShadow: '0 0 18px rgba(255,232,0,0.18)' }}
          >
            <PhoneCall size={15} /> Book Consulting
          </Link>
        </div>

        {/* Cross-links to key pages */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { to: '/find', icon: '🔍', title: 'Find My Best Tool', desc: 'Answer a few questions, get your perfect match.' },
            { to: '/compare', icon: '⚖️', title: 'Compare Tools', desc: 'Side-by-side feature, pricing and fit analysis.' },
            { to: '/best?sort=overall', icon: '🏆', title: 'Rankings Hub', desc: 'Use cases, top-rated picks, and budget views in one matrix.' },
          ].map((card) => (
            <Link
              key={card.to}
              to={card.to}
              className="p-5 rounded-xl transition-all hover:scale-[1.02]"
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
            >
              <div className="text-2xl mb-2">{card.icon}</div>
              <div className="font-bold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{card.title}</div>
              <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{card.desc}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CategoriesPage;
